/**
 * Nov-2026 (User-Wunsch) — Preisetiketten als eigener Tab in der Warenwirtschaft.
 * Mit Vorschau, Stückzahl je Artikel und wählbarem Etikettenformat
 * (Bögen wie Zweckform 3660/3475 oder Endlos-Rollendrucker).
 */
import { useEffect, useMemo, useState } from "react";
import { Tags, Loader2, Info, Eye, RotateCcw, Boxes } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const fmtEur = (v) => (v || v === 0 ? Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" }) : "—");

export default function PriceLabelsSection({ pendingArticles = [], onPendingConsumed }) {
    const [locations, setLocations] = useState([]);
    const [loc, setLoc] = useState("");
    const [ownership, setOwnership] = useState("consignment");
    const [inStock, setInStock] = useState(true);
    const [format, setFormat] = useState("zweckform_3660");
    const [formats, setFormats] = useState([]);
    // Nov-2026 (User-Wunsch): auch andere/freie Größen
    const [customW, setCustomW] = useState("97");
    const [customH, setCustomH] = useState("67,7");
    const [customKind, setCustomKind] = useState("sheet");
    // Nov-2026 (User-Wunsch): auf angefangenem Bogen an Position X starten
    const [startPos, setStartPos] = useState(1);
    const [items, setItems] = useState(null);
    const [qty, setQty] = useState({});
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/org/locations")
            .then((r) => setLocations(r.data?.locations || []))
            .catch(() => setLocations([]));
    }, []);

    // Jun-2026 (User-Wunsch): Artikel aus der Artikelliste kommen vorausgewählt an
    useEffect(() => {
        if (!pendingArticles.length) return;
        setItems((prev) => {
            const existing = prev || [];
            const add = pendingArticles.filter((a) => !existing.some((x) => x.id === a.id));
            return [...add, ...existing];
        });
        setQty((prev) => ({ ...Object.fromEntries(pendingArticles.map((a) => [a.id, 1])), ...prev }));
        toast.success(`${pendingArticles.length} Artikel zum Etikettenbogen hinzugefügt`);
        onPendingConsumed && onPendingConsumed();
    }, [pendingArticles]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPreview = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ in_stock: inStock ? "1" : "0" });
            if (ownership) params.set("ownership", ownership);
            if (loc) params.set("dealer_id", loc);
            const { data } = await api.get(`/articles/price-labels/items?${params}`);
            setItems(data.items || []);
            setFormats(data.formats || []);
            setQty(Object.fromEntries((data.items || []).map((a) => [a.id, 1])));
            if ((data.items || []).length === 0) toast.warning("Keine Artikel für die gewählten Filter");
        } catch {
            toast.error("Vorschau konnte nicht geladen werden");
        } finally { setLoading(false); }
    };

    const fmt = useMemo(() => {
        if (format === "custom") {
            const w = parseFloat(String(customW).replace(",", ".")) || 0;
            const h = parseFloat(String(customH).replace(",", ".")) || 0;
            const perPage = customKind === "roll" ? 1 : Math.max(1, Math.floor(210 / (w || 210))) * Math.max(1, Math.floor(297 / (h || 297)));
            return { key: "custom", kind: customKind, w, h, per_page: perPage };
        }
        return formats.find((f) => f.key === format);
    }, [formats, format, customW, customH, customKind]);
    const totalLabels = useMemo(() => Object.values(qty).reduce((s, v) => s + (parseInt(v, 10) > 0 ? parseInt(v, 10) : 0), 0), [qty]);
    const skip = fmt?.kind === "sheet" ? Math.max(0, Math.min((parseInt(startPos, 10) || 1) - 1, (fmt?.per_page || 1) - 1)) : 0;
    const pages = fmt ? Math.ceil((totalLabels + skip) / (fmt.per_page || 1)) : 0;

    const setAll = (v) => setQty(Object.fromEntries((items || []).map((a) => [a.id, v === "stock" ? Math.max(0, a.stock_qty || 0) : v])));

    const createPdf = async (openInline) => {
        if (totalLabels === 0) { toast.error("Bitte mindestens eine Stückzahl > 0 setzen"); return; }
        setBusy(true);
        try {
            const r = await api.post("/articles/price-labels.pdf", {
                dealer_id: loc || null, ownership: ownership || null, in_stock: inStock,
                format, quantities: qty,
                start_pos: fmt?.kind === "sheet" ? (parseInt(startPos, 10) || 1) : 1,
                custom: format === "custom" ? { w: customW, h: customH, kind: customKind } : undefined,
            }, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            if (openInline) {
                window.open(url, "_blank", "noopener");
            } else {
                const a = document.createElement("a");
                a.href = url; a.download = "Preisetiketten.pdf"; a.click();
            }
            toast.success(`${totalLabels} Etikett(en) · ${fmt?.kind === "roll" ? `${totalLabels} Rollen-Etiketten` : `${pages} Bogen/Bögen`}`);
        } catch {
            toast.error("Etiketten-PDF fehlgeschlagen");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-5xl" data-testid="wms-labels-section">
            <div className="bg-white border-2 border-[#111827] rounded-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Tags className="w-5 h-5 text-[#111827]" />
                    <h3 className="font-display font-black text-lg">Preisetiketten (Barcode)</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Vorschau laden, Stückzahl je Artikel festlegen, Etikettenformat wählen (Bögen wie <b>Zweckform 3660</b> oder Endlos-Rolle) — dann PDF ansehen oder herunterladen.</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Standort</label>
                        <select value={loc} onChange={(e) => setLoc(e.target.value)}
                            className="mt-1 w-full h-10 text-sm border-2 border-gray-200 rounded-sm px-2 bg-white" data-testid="wms-labels-location-select">
                            <option value="">Alle Standorte</option>
                            {locations.map((l) => (
                                <option key={l.dealer_id} value={l.dealer_id}>
                                    {[l.site_code, l.street || l.company_name, l.city].filter(Boolean).join(" · ")}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Bestandsart</label>
                        <select value={ownership} onChange={(e) => setOwnership(e.target.value)}
                            className="mt-1 w-full h-10 text-sm border-2 border-gray-200 rounded-sm px-2 bg-white" data-testid="wms-labels-ownership-select">
                            <option value="consignment">Kommission</option>
                            <option value="own">Eigenbestand</option>
                            <option value="">Alle Bestandsarten</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Etikettenformat</label>
                        <select value={format} onChange={(e) => setFormat(e.target.value)}
                            className="mt-1 w-full h-10 text-sm border-2 border-gray-200 rounded-sm px-2 bg-white" data-testid="wms-labels-format-select">
                            {(formats.length ? formats : [
                                { key: "zweckform_3660", label: "Zweckform 3660 — 97×67,7 mm (8 je A4-Bogen)" },
                                { key: "zweckform_3475", label: "Zweckform 3475 — 70×36 mm (24 je A4-Bogen)" },
                                { key: "zweckform_3653", label: "Zweckform 3653 — 105×42,3 mm (14 je A4-Bogen)" },
                                { key: "zweckform_3651", label: "Zweckform 3651 — 52,5×29,7 mm (40 je A4-Bogen)" },
                                { key: "endlos_62x29", label: "Endlos-Rolle — 62×29 mm (Brother DK-11209)" },
                                { key: "endlos_103x64", label: "Endlos-Rolle — 103×64 mm (Brother DK-11241)" },
                                { key: "endlos_57x32", label: "Endlos-Rolle — 57×32 mm (Dymo 11354)" },
                            ]).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                            <option value="custom">Eigene Größe … (Breite × Höhe in mm)</option>
                        </select>
                        {format === "custom" && (
                            <div className="flex items-center gap-2 mt-2" data-testid="wms-labels-custom-format">
                                <input type="text" inputMode="decimal" value={customW} onChange={(e) => setCustomW(e.target.value)}
                                    className="w-20 h-9 border-2 border-gray-200 rounded-sm px-2 text-right bg-white" placeholder="Breite"
                                    data-testid="wms-labels-custom-w" />
                                <span className="text-xs text-gray-500">×</span>
                                <input type="text" inputMode="decimal" value={customH} onChange={(e) => setCustomH(e.target.value)}
                                    className="w-20 h-9 border-2 border-gray-200 rounded-sm px-2 text-right bg-white" placeholder="Höhe"
                                    data-testid="wms-labels-custom-h" />
                                <span className="text-xs text-gray-500">mm</span>
                                <select value={customKind} onChange={(e) => setCustomKind(e.target.value)}
                                    className="h-9 text-sm border-2 border-gray-200 rounded-sm px-2 bg-white" data-testid="wms-labels-custom-kind">
                                    <option value="sheet">A4-Bogen (Raster automatisch)</option>
                                    <option value="roll">Endlos-Rolle</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                    <label className="flex items-center gap-2 text-[12px] text-gray-700 cursor-pointer select-none" data-testid="wms-labels-instock-toggle">
                        <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="w-4 h-4 accent-[#111827]" />
                        <span>Nur Artikel <b>mit Bestand</b></span>
                    </label>
                    {fmt?.kind !== "roll" && (
                        <label className="flex items-center gap-2 text-[12px] text-gray-700 select-none" data-testid="wms-labels-startpos"
                            title="Für angefangene Bögen: Position 1 = oben links, zeilenweise gezählt — davor liegende Etiketten bleiben frei">
                            <span>Start bei Etikett-Position</span>
                            <input type="number" min="1" max={fmt?.per_page || 40} value={startPos}
                                onChange={(e) => setStartPos(e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value, 10) || 1))}
                                className="w-16 h-8 border-2 border-gray-200 rounded-sm px-2 text-right bg-white"
                                data-testid="wms-labels-startpos-input" />
                            <span className="text-gray-400">von {fmt?.per_page || "…"} je Bogen</span>
                        </label>
                    )}
                    <Button onClick={loadPreview} disabled={loading} variant="outline"
                        className="h-9 rounded-sm font-bold border-2 border-[#111827]" data-testid="wms-labels-preview-btn">
                        {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                        Vorschau laden
                    </Button>
                </div>
            </div>

            {items !== null && (
                <div className="bg-white border border-gray-200 rounded-sm mt-4" data-testid="wms-labels-preview">
                    <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
                        <span className="font-bold text-sm">{items.length} Artikel</span>
                        <span className="text-[12px] text-gray-500" data-testid="wms-labels-summary">
                            → <b>{totalLabels}</b> Etikett(en){fmt ? (fmt.kind === "roll" ? " auf Endlos-Rolle" : ` auf ${pages} A4-Bogen/Bögen (${fmt.per_page}/Bogen)`) : ""}
                        </span>
                        <span className="ml-auto flex gap-1.5">
                            <button type="button" onClick={() => setAll(1)} className="text-[11px] underline text-gray-500 hover:text-gray-700" data-testid="wms-labels-all-1">alle = 1</button>
                            <button type="button" onClick={() => setAll("stock")} className="text-[11px] underline text-gray-500 hover:text-gray-700 inline-flex items-center gap-0.5" data-testid="wms-labels-all-stock"><Boxes className="w-3 h-3" /> = Bestand</button>
                            <button type="button" onClick={() => setAll(0)} className="text-[11px] underline text-gray-500 hover:text-gray-700 inline-flex items-center gap-0.5" data-testid="wms-labels-all-0"><RotateCcw className="w-3 h-3" /> alle = 0</button>
                        </span>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-gray-50">
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                                    <th className="px-4 py-2">SKU</th>
                                    <th className="px-2 py-2">Name</th>
                                    <th className="px-2 py-2">Lager</th>
                                    <th className="px-2 py-2 text-right">VK brutto</th>
                                    <th className="px-2 py-2 text-right">Bestand</th>
                                    <th className="px-4 py-2 text-right">Stückzahl</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((a) => (
                                    <tr key={a.id} className="border-t border-gray-100" data-testid={`wms-labels-row-${a.sku}`}>
                                        <td className="px-4 py-1.5 font-mono text-[12px]">{a.sku}</td>
                                        <td className="px-2 py-1.5">{a.name}</td>
                                        <td className="px-2 py-1.5 text-[12px] text-gray-500">{a.location || "—"}</td>
                                        <td className="px-2 py-1.5 text-right font-bold">{fmtEur(a.sell_price_gross)}</td>
                                        <td className="px-2 py-1.5 text-right text-[12px]">{a.stock_qty ?? "—"}</td>
                                        <td className="px-4 py-1.5 text-right">
                                            <input type="number" min="0" max="200" value={qty[a.id] ?? 1}
                                                onChange={(e) => setQty((p) => ({ ...p, [a.id]: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                                className="w-20 h-8 border-2 border-gray-200 rounded-sm px-2 text-right bg-white"
                                                data-testid={`wms-labels-qty-${a.sku}`} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap justify-end gap-2">
                        <Button onClick={() => createPdf(true)} disabled={busy || totalLabels === 0} variant="outline"
                            className="h-10 rounded-sm font-bold border-2 border-[#111827]" data-testid="wms-labels-open-pdf-btn">
                            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                            PDF-Vorschau öffnen
                        </Button>
                        <Button onClick={() => createPdf(false)} disabled={busy || totalLabels === 0}
                            className="h-10 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                            data-testid="wms-labels-download-btn">
                            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Tags className="w-4 h-4 mr-1.5" />}
                            Etiketten-PDF herunterladen
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
